/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lease_transactions-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lease_transactions-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lease_transactions-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, transaction_type_id, transaction_status_id, source_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, permission_level_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, lease_subtype_id, rent_structure_id, archived_by_user_profile_id, archive_batch_id
 * - Sortable fields: execution_date, commencement_date, expiration_date, created_at, updated_at
 * - Required fields: id, permission_level_id, is_colliers_lease
 * - Readonly fields: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lease_transactions-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lease_transactions-api-client.ts (API client)
 * - client/api/generated_schemas/lease_transactions.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lease_transactions-entity-config.ts
 * Public fields: 49/49 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LeaseTransactionsClient, LeaseTransactionsQueryOptions } from '../generated_clients/generated-lease_transactions-api-client.js';
import {
  LeaseTransactionsInsert,
  LeaseTransactionsUpdate,
  LeaseTransactionsFieldConfig
} from '../../types/generated_schemas/generated-lease_transactions-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lease_transactions_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const leaseTransactionsKeys = {
  all: ['lease_transactions'] as const,
  lists: () => [...leaseTransactionsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LeaseTransactionsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...leaseTransactionsKeys.lists(), { filters, ...page }] as const,
  details: () => [...leaseTransactionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...leaseTransactionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...leaseTransactionsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...leaseTransactionsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...leaseTransactionsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...leaseTransactionsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLeaseTransactionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LeaseTransactionsClient();
};

/**
 * Hook to get all lease_transactions with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lease_transactions data
 */
export const useLeaseTransactionsList = (options?: LeaseTransactionsQueryOptions) => {
  const client = useLeaseTransactionsClient();
  
  return useQuery({
    queryKey: leaseTransactionsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lease_transaction by ID
 *
 * @param id - The lease_transaction ID
 * @returns React Query result with single lease_transaction data
 */
export const useLeaseTransactions = (id: string) => {
  const client = useLeaseTransactionsClient();
  
  return useQuery({
    queryKey: leaseTransactionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lease_transactions
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lease_transactions_business_hooks/
 */

/**
 * Hook to create a new lease_transaction
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, permission_level_id, is_colliers_lease
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for creating lease_transaction
 */
export const useCreateLeaseTransactions = () => {
  const client = useLeaseTransactionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LeaseTransactionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaseTransactionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing lease_transaction
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for updating lease_transaction
 */
export const useUpdateLeaseTransactions = () => {
  const client = useLeaseTransactionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LeaseTransactionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: leaseTransactionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: leaseTransactionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lease_transaction
 *
 * @returns React Query mutation for deleting lease_transaction
 */
export const useDeleteLeaseTransactions = () => {
  const client = useLeaseTransactionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaseTransactionsKeys.all });
    },
  });
};

/**
 * Hook to count lease_transactions with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, transaction_type_id, transaction_status_id, source_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, permission_level_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, lease_subtype_id, rent_structure_id, archived_by_user_profile_id, archive_batch_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLeaseTransactionsCount = (filter?: Partial<Record<typeof LeaseTransactionsFieldConfig.filterable[number], any>>) => {
  const client = useLeaseTransactionsClient();
  
  return useQuery({
    queryKey: leaseTransactionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLeaseTransactionsFieldConfig = () => {
  return LeaseTransactionsFieldConfig;
};